import { useMutation } from '@tanstack/react-query';
import { Scale } from 'lucide-react';

import { suggestDisposition } from '@/api/findings';
import type { SuggestionOut } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { citationLabel, formatUsdCost } from '@/lib/kb-cost';

import { useAuth } from '../auth/AuthContext';

/** Reason codes are stored as snake_case; show them as readable prose. */
function reasonCodeLabel(code: string): string {
  return code.replace(/_/g, ' ');
}

/** "Suggest disposition" (design §7.3) — decision support: a CRA-grounded
 *  suggested evaluation (approve / reject / needs review) with rationale,
 *  citations, and confidence. Advisory only — it never writes the finding's
 *  status; the practitioner accepts or overrides. Renders nothing until asked. */
export function SuggestDisposition({ findingId }: { findingId: string }) {
  const { getIdToken } = useAuth();

  const suggest = useMutation({
    mutationFn: async (): Promise<SuggestionOut> =>
      suggestDisposition(findingId, (await getIdToken()) ?? ''),
  });

  const suggestion = suggest.data;

  return (
    <section className="grid gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wide text-brand-slate">Suggest disposition</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => suggest.mutate()}
          disabled={suggest.isPending}
        >
          {suggest.isPending ? (
            <Spinner label="Assessing…" />
          ) : (
            <>
              <Scale size={14} strokeWidth={1.75} /> {suggestion ? 'Reassess' : 'Suggest'}
            </>
          )}
        </Button>
      </div>

      {suggest.isError ? (
        <p className="text-sm text-brand-slate">Couldn't generate a suggestion. Please try again.</p>
      ) : null}

      {suggestion ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge kind="finding" status={suggestion.suggested_disposition} />
            {suggestion.reason_code ? (
              <span className="text-xs text-brand-slate">
                {reasonCodeLabel(suggestion.reason_code)}
              </span>
            ) : null}
            {suggestion.abstained ? (
              <span className="text-xs text-brand-slate">
                low confidence — routed to review
              </span>
            ) : null}
          </div>

          {/* Advisory framing — the tool suggests; the practitioner decides. */}
          <p className="text-xs text-brand-slate">
            Suggestion only — this does not change the finding&apos;s status.
          </p>

          {suggestion.grounded ? (
            <p className="whitespace-pre-wrap text-sm text-brand-navy">{suggestion.rationale}</p>
          ) : (
            <p className="text-sm text-brand-slate">
              The corpus didn&apos;t contain enough CRA guidance to decide this without guessing.
            </p>
          )}

          {suggestion.citations.length > 0 ? (
            <ul className="grid gap-1">
              {suggestion.citations.map((c, i) => (
                <li key={`${c.doc_code}-${c.para_range}-${i}`} className="text-sm">
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-royal hover:underline"
                  >
                    {citationLabel(c.doc_code, c.para_range)}
                  </a>
                  {c.title ? <span className="text-brand-slate"> — {c.title}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}

          <p className="text-xs text-brand-slate">
            confidence {Math.round(Number(suggestion.confidence) * 100)}% · {suggestion.model} ·{' '}
            {formatUsdCost(Number(suggestion.cost_usd))}
          </p>
        </div>
      ) : null}
    </section>
  );
}
